import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";

type Result = {
  id?: string;
  full_name?: string;
  birth_date?: string;
  mrn?: string;
  phone_masked?: string;
  identifier_type?: string;
  identifier_masked?: string;
  chart_access?: "granted" | "needs_relationship";
};

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/** A birth date as a person says it, not as a database stores it. */
function born(iso: string) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleDateString("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * A cell with nothing in it, said once.
 *
 * Not a dash per empty column. A young clinic has a name and little else, and
 * seven columns of dashes reads as a query that failed rather than a practice
 * that is three weeks old. The mark is dim, and what it means is on it for a
 * screen reader rather than left to the shape.
 */
function Unrecorded() {
  return (
    <span className="unrecorded" aria-label="Not recorded">
      <span aria-hidden="true">—</span>
    </span>
  );
}

/**
 * Find a patient of the active clinic (CLN-F-040).
 *
 * The way into a chart, and nothing else: demographics, masked, and a link.
 * No clinical content appears here and no preview of it, because the list is
 * open to the front desk and a chart is not.
 *
 * Identity numbers and telephone numbers arrive from the server already cut
 * to their last four characters. That is a property of the response and not
 * of this screen — a mask applied here would be a full identifier sent to the
 * browser and hidden with CSS, which is not a mask at all.
 *
 * A row says whether the chart is already open to this caller, which saves a
 * click and grants nothing: opening it asks the server again.
 */
export function PatientSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Result[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // A search runs while a doctor is still typing, so it waits for a pause.
    // Without it every keystroke is a query against patient names.
    const timer = setTimeout(() => {
      let cancelled = false;
      setLoading(true);

      api
        .GET("/portal/patients", {
          params: { query: query ? { q: query } : {}, header: clinicHeader() },
        })
        .then(({ data, error: failed }) => {
          if (cancelled) return;
          setLoading(false);
          if (failed) {
            setError(readError(failed).message);
            setResults([]);
            return;
          }
          setError(null);
          // The list arrives as an array, not wrapped in `items`. Every
          // other portal list is wrapped, which is exactly why this one is
          // worth a line: the compiler caught it here, and the same
          // assumption shipped silently on the escalations panel once.
          setResults((data ?? []) as Result[]);
        });

      return () => {
        cancelled = true;
      };
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  const sparse = !query && results.length > 0 && results.length < 5;
  const found = results.length;

  return (
    <section className="patients">
      <header className="screen-head">
        <h1>Patients</h1>
        <p className="screen-lead">
          Search and select a patient to open their record.
        </p>
      </header>

      <div className="patients-search">
        <svg className="patients-search-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3" />
        </svg>
        <input
          className="search"
          type="search"
          aria-label="Search patients by IC number, name, MRN or email"
          placeholder="IC number, name, MRN, or email"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoFocus
        />
        {query && (
          <button type="button" className="quiet" onClick={() => setQuery("")}>
            Clear
          </button>
        )}
      </div>
      <p className="patients-hint">
        Four ways in, because the desk may only know one of them.
      </p>

      <ErrorNote message={error} />

      {/* Rows rather than a spinner. The shell stays still and the table does
          not jump when the answer lands, which it did when a line of text was
          swapped for a list. */}
      {loading && (
        <div className="data-table-wrap" aria-busy="true" aria-live="polite">
          <span className="visually-hidden">Searching</span>
          <ul className="patient-skeleton">
            {[0, 1, 2, 3, 4].map((row) => (
              <li key={row}>
                <span className="sk sk-mark" />
                <span className="sk sk-line" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {!loading && found === 0 && (
        <p className="empty">
          {query
            ? "No patient of this clinic matches that. Check the spelling, or try an IC number or MRN."
            : "No patients yet. They appear here once the desk registers somebody, or once a patient signs up with the clinic's code."}
        </p>
      )}

      {!loading && found > 0 && (
        <>
          <p className="patients-count" aria-live="polite">
            {query
              ? `${found} ${found === 1 ? "patient matches" : "patients match"} “${query}”`
              : `${found} ${found === 1 ? "patient" : "patients"}`}
          </p>

          {sparse && (
            // A new clinic has three patients and a table of empty columns,
            // which reads as a broken screen rather than a young one.
            <p className="patients-sparse">
              This clinic is new. Identity numbers and MRNs fill in as the desk
              records them.
            </p>
          )}

          <div className="data-table-wrap">
            <table className="data-table">
              <caption className="visually-hidden">
                Patients of this clinic. Identity and telephone numbers show
                their last four characters only.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Identity</th>
                  <th scope="col">MRN</th>
                  <th scope="col">Born</th>
                  <th scope="col">Telephone</th>
                  <th scope="col">
                    <span className="visually-hidden">Chart</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.map((patient) => (
                  <tr key={patient.id}>
                    <td className="pt-name">
                      <span className="avatar small" aria-hidden="true">
                        {initials(patient.full_name ?? "")}
                      </span>
                      <Link
                        to={`/patients/${patient.id}`}
                        className="patient-name"
                      >
                        {patient.full_name}
                      </Link>
                    </td>

                    <td className="pt-id">
                      {patient.identifier_masked ? (
                        <>
                          <span className="pt-kind">
                            {patient.identifier_type || "ID"}
                          </span>
                          <span className="pt-value">
                            {patient.identifier_masked}
                          </span>
                        </>
                      ) : (
                        <Unrecorded />
                      )}
                    </td>

                    <td className="pt-mrn">
                      {patient.mrn ? patient.mrn : <Unrecorded />}
                    </td>

                    <td className="pt-born">
                      {patient.birth_date ? born(patient.birth_date) : <Unrecorded />}
                    </td>

                    <td className="pt-phone">
                      {patient.phone_masked ? patient.phone_masked : <Unrecorded />}
                    </td>

                    <td className="pt-go">
                      <span
                        className={
                          patient.chart_access === "granted"
                            ? "tag open"
                            : "tag closed"
                        }
                      >
                        {patient.chart_access === "granted"
                          ? "Open"
                          : "Needs access"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
